import type { DecodedToken, OAuthTokenResponse, User } from '../features/auth/types';
import {
  ACCESS_TOKEN_KEY,
  SPA_REFRESH_ENDPOINT,
  TOKEN_EXPIRY_KEY,
} from '../features/auth/constants';
import { LocalStorageKeys } from './localStorageService';

let refreshingPromise: Promise<OAuthTokenResponse> | null = null;

/**
 * Single-flight OAuth refresh using fetch to avoid recursion
 */
export function refreshOAuthTokens(): Promise<OAuthTokenResponse> {
  if (refreshingPromise) return refreshingPromise;

  refreshingPromise = (async () => {
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const res = await fetch(`${baseUrl}${SPA_REFRESH_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!res.ok) {
        throw new Error(`Refresh failed: ${res.status}`);
      }

      const data = (await res.json()) as OAuthTokenResponse;

      const now = Math.floor(Date.now() / 1000);
      localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
      localStorage.setItem(TOKEN_EXPIRY_KEY, String(now + data.expires_in));

      // Notify Redux store so state.auth.accessToken never goes stale
      sessionRefreshedCallback?.(data);

      return data;
    } finally {
      refreshingPromise = null;
    }
  })();

  return refreshingPromise;
}

let logoutCallback: (() => void) | null = null;

export const setLogoutCallback = (callback: () => void) => {
  logoutCallback = callback;
};

let sessionRefreshedCallback: ((tokenResponse: OAuthTokenResponse) => void) | null = null;

export const setSessionRefreshedCallback = (cb: (tokenResponse: OAuthTokenResponse) => void) => {
  sessionRefreshedCallback = cb;
};

export const clearAuthStorage = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(LocalStorageKeys.CachedFinanceComposeClientSelection);
};

export const tryRefreshToken = async (): Promise<string> => {
  try {
    const data = await refreshOAuthTokens();
    return data.access_token;
  } catch {
    clearAuthStorage();
    logoutCallback?.();
    throw new Error('Session expired');
  }
};

export const enqueueBackgroundTokenRefresh = () => {
  void refreshOAuthTokens().catch(() => {});
};

export const decodeToken = (token: string): DecodedToken | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

export const isTokenValid = (): boolean => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!token) return false;

  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiry) return false;

  const currentTime = Math.floor(Date.now() / 1000);
  return parseInt(expiry, 10) > currentTime;
};

export const getUserFromToken = (token: string): User | null => {
  const decoded = decodeToken(token);
  if (!decoded) return null;

  return {
    id: decoded.nameid,
    username: decoded.unique_name,
  };
};

const normalizeRolesClaim = (roles: unknown): string[] => {
  if (!roles) return [];

  if (Array.isArray(roles)) {
    return roles
      .filter((role): role is string => typeof role === 'string')
      .map((role) => role.trim().toLowerCase())
      .filter(Boolean);
  }

  if (typeof roles === 'string') {
    return roles
      .split(',')
      .map((role) => role.trim().toLowerCase())
      .filter(Boolean);
  }

  return [];
};

export const getRolesFromToken = (token: string): string[] => {
  const decoded = decodeToken(token);
  if (!decoded) return [];

  const tokenPayload = decoded as unknown as Record<string, unknown>;
  return normalizeRolesClaim(tokenPayload.role ?? tokenPayload.roles);
};

export const storeToken = (tokenResponse: OAuthTokenResponse): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, tokenResponse.access_token);

  const currentTime = Math.floor(Date.now() / 1000);
  const expiryTime = currentTime + tokenResponse.expires_in;
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
};

export const getStoredToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};
